import { useCallback, useEffect, useRef, useState } from 'react';
import { HighlightState } from '../types';

const ACTIVE_DURATION_MS = 10000;

export function useHighlightSync() {
  const [highlight, setHighlight] = useState<HighlightState>({
    hoveredLineId: null,
    activeLineId: null,
  });

  const lineRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const activeTimer = useRef<ReturnType<typeof setTimeout>>();

  // Cleanup timer on unmount
  useEffect(() => {
    return () => clearTimeout(activeTimer.current);
  }, []);

  const registerLineRef = useCallback((id: string, el: HTMLTableRowElement | null) => {
    if (el) {
      lineRefs.current.set(id, el);
    } else {
      lineRefs.current.delete(id);
    }
  }, []);

  const hoverLine = useCallback((id: string | null) => {
    setHighlight((prev) => ({ ...prev, hoveredLineId: id }));
  }, []);

  const clickLine = useCallback((id: string | null) => {
    clearTimeout(activeTimer.current);
    setHighlight((prev) => ({ ...prev, activeLineId: id }));
    if (id) {
      activeTimer.current = setTimeout(() => {
        setHighlight((prev) => ({ ...prev, activeLineId: null }));
      }, ACTIVE_DURATION_MS);
    }
  }, []);

  const scrollToLine = useCallback((id: string) => {
    const el = lineRefs.current.get(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  return {
    highlight,
    hoverLine,
    clickLine,
    scrollToLine,
    registerLineRef,
  };
}
